/**
 * Admin Application - Rebuilt with Unified Layout System
 *
 * Uses new architecture:
 * - RootLayout: Handles all auth + providers (eliminates 65 lines of boilerplate)
 * - AppShell: Provides sidebar navigation + header (replaces custom AppLayout)
 * - Bundle mode handled automatically by layout system
 *
 * Before: 116 lines with duplicate auth, manual providers, custom layout
 * After: ~80 lines, clean and maintainable
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { RootLayout, AppShell, type SidebarNavItem } from '@room/shared-ui';
import AdminPipeline from '@/pages/Admin/Pipeline';
import AdminSystemSettings from '@/pages/Admin/SystemSettings';
import AdminSystemUsers from '@/pages/Admin/SystemUsers';
import AdminDevConsole from '@/pages/Admin/DevConsole';
import AdminConfigurations from '@/pages/Admin/Configurations';
import AdminBundleVersion from '@/pages/Admin/BundleVersion';
import DevConsole from '@/components/DevConsole';
import { isBundleMode } from '@room/shared-ui';

/**
 * Admin sidebar navigation items
 * Defines all admin sections in order of appearance
 */
const adminNavItems: SidebarNavItem[] = [
  {
    id: 'pipeline',
    label: 'Pipeline',
    icon: '📈',
    path: '/pipeline',
  },
  {
    id: 'configurations',
    label: 'Configurations',
    icon: '⚙️',
    path: '/configurations',
  },
  {
    id: 'system-settings',
    label: 'System Settings',
    icon: '🛠️',
    path: '/system-settings',
  },
  {
    id: 'system-users',
    label: 'System Users',
    icon: '👥',
    path: '/system-users',
  },
  {
    id: 'bundle-version',
    label: 'Bundle Version',
    icon: '📦',
    path: '/bundle-version',
  },
  {
    id: 'dev-console',
    label: 'Dev Console',
    icon: '🧰',
    path: '/dev-console',
  },
];

/**
 * Route titles for default header display
 */
const routeTitles: Record<string, string> = {
  '/': 'Admin',
  '/pipeline': 'Pipeline',
  '/configurations': 'Configurations',
  '/system-settings': 'System Settings',
  '/system-users': 'System Users',
  '/bundle-version': 'Bundle Version',
  '/dev-console': 'Dev Console',
};

export function App() {
  const isRunningInBundle = isBundleMode();

  return (
    <RootLayout appName="Admin">
      <BrowserRouter basename="/admin">
        <AppShell
          appName="Admin"
          sidebarItems={adminNavItems}
          routeTitles={routeTitles}
          showSidebarInBundle={true}
          showHeaderInBundle={true}
          showBottomNavInBundle={true}
        >
          <Routes>
            {/* Default route - redirect to pipeline */}
            <Route path="/" element={<Navigate to="/pipeline" replace />} />

            {/* Admin sections */}
            <Route path="/pipeline" element={<AdminPipeline />} />
            <Route path="/configurations" element={<AdminConfigurations />} />
            <Route path="/system-settings" element={<AdminSystemSettings />} />
            <Route path="/system-users" element={<AdminSystemUsers />} />
            <Route path="/bundle-version" element={<AdminBundleVersion />} />
            <Route path="/dev-console" element={<AdminDevConsole />} />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/pipeline" replace />} />
          </Routes>
        </AppShell>
      </BrowserRouter>

      {/* Dev console - only in standalone mode */}
      {!isRunningInBundle && <DevConsole />}
    </RootLayout>
  );
}
